// Flussdiagramm-Interpreter im Browser -- muss sich exakt wie flowlab/expr.py und
// flowlab/interpreter.py verhalten (gemeinsame Testfälle in tests/fixtures/flows.json).
// Zusätzlich: Runner zum schrittweisen Ausführen im Editor.

export const MAX_INT = Number.MAX_SAFE_INTEGER; // 2^53 - 1, wie im Python-Original

export const BOX_TYPES = ["verarbeitung", "eingabe", "ausgabe", "entscheidung"] as const;
export const NODE_TYPES = ["start", "ende", ...BOX_TYPES] as const;
export type NodeType = (typeof NODE_TYPES)[number];

export interface FlowNode { id: string; type: string; text?: string; x?: number; y?: number }
export interface FlowEdge { from: string; to: string }
export interface Flow { nodes: FlowNode[]; edges: FlowEdge[] }

export interface Testfall { eingabe: number[]; ausgabe: number[] }
export interface Task {
  id: string; titel: string; text: string;
  eingaben: string[]; ausgaben: string[]; testfaelle: Testfall[];
  boxen: Record<string, number>; par: number | null; max_schritte: number;
  hinweise: string[]; block: string | null;
  optional?: boolean; // Zusatzaufgabe für Schnelle
}

export interface Problem { code: string; message: string; ref: string | null }

// ------------------------------------------------------------------------- Ausdrücke

export class ExprError extends Error {}
export class RunError extends Error {}

export type Node =
  | { k: "num"; value: number }
  | { k: "var"; name: string }
  | { k: "text"; value: string }
  | { k: "neg"; arg: Node }
  | { k: "not"; arg: Node }
  | { k: "bin"; op: string; left: Node; right: Node };

const ARITH = new Set(["+", "-", "*", "/", "mod"]);
const COMPARE = new Set(["==", "!=", "<", "<=", ">", ">="]);

const NAME = "[A-Za-zÄÖÜäöüß_][A-Za-z0-9ÄÖÜäöüß_]*";
const NAME_RE = new RegExp(`^${NAME}$`);
const WORDS: Record<string, string> = {
  und: "and", and: "and", oder: "or", or: "or", nicht: "not", not: "not", mod: "mod", div: "/",
};
const SYMBOLS: Record<string, string> = {
  "<=": "<=", ">=": ">=", "==": "==", "!=": "!=", "<>": "!=", "&&": "and", "||": "or",
  "≤": "<=", "≥": ">=", "≠": "!=", "<": "<", ">": ">", "=": "==",
  "+": "+", "-": "-", "−": "-", "*": "*", "·": "*", "×": "*", "/": "/", ":": "/",
  "%": "mod", "!": "not", "(": "(", ")": ")", ",": ",",
};
const TOKEN_RE = new RegExp(
  String.raw`\s*(?:"([^"]*)"|„([^“”"]*)[“”"]|“([^”]*)”|'([^']*)'` +
  String.raw`|([0-9]+(?:[.,][0-9]+)?)` +
  `|(${NAME})` +
  String.raw`|(<=|>=|==|!=|<>|&&|\|\||.))`, "y");

type Tok = [kind: "text" | "num" | "name" | "op", val: string | number];

function tokenize(src: string): Tok[] {
  const tokens: Tok[] = [];
  const text = src.trimEnd();
  let pos = 0;
  while (pos < text.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(text)!;
    pos = TOKEN_RE.lastIndex;
    const [, s1, s2, s3, s4, number, word, sym] = m;
    const str = [s1, s2, s3, s4].find(s => s !== undefined);
    if (str !== undefined) {
      tokens.push(["text", str]);
    } else if (number !== undefined) {
      if (!/^[0-9]+$/.test(number)) throw new ExprError(`Nur ganze Zahlen erlaubt, nicht ${number}`);
      if (Number(number) > MAX_INT) throw new ExprError(`Zahl ${number} ist zu groß`);
      tokens.push(["num", Number(number)]);
    } else if (word !== undefined) {
      const low = word.toLowerCase();
      tokens.push(low in WORDS ? ["op", WORDS[low]] : ["name", word]);
    } else if (sym !== undefined && sym.trim()) {
      if (!(sym in SYMBOLS)) throw new ExprError(`Unbekanntes Zeichen '${sym}'`);
      tokens.push(["op", SYMBOLS[sym]]);
    }
  }
  return tokens;
}

function show(tok: Tok | undefined): string {
  if (!tok) return "Ende";
  return tok[0] === "text" ? `"${tok[1]}"` : `'${tok[1]}'`;
}

class Parser {
  tokens: Tok[];
  i = 0;
  constructor(text: string) { this.tokens = tokenize(text); }

  peek(): Tok | undefined { return this.tokens[this.i]; }
  peekOp(): string | null {
    const t = this.peek();
    return t && t[0] === "op" ? (t[1] as string) : null;
  }
  take(): Tok {
    const t = this.peek();
    if (!t) throw new ExprError("Ausdruck endet unerwartet");
    this.i++;
    return t;
  }
  atEnd(): boolean { return this.i >= this.tokens.length; }
  expectEnd(): void { if (!this.atEnd()) throw new ExprError(`Unerwartetes ${show(this.peek())}`); }

  expr(): Node {
    let node = this.conj();
    while (this.peekOp() === "or") { this.take(); node = { k: "bin", op: "or", left: node, right: this.conj() }; }
    return node;
  }
  conj(): Node {
    let node = this.neg();
    while (this.peekOp() === "and") { this.take(); node = { k: "bin", op: "and", left: node, right: this.neg() }; }
    return node;
  }
  neg(): Node {
    if (this.peekOp() === "not") { this.take(); return { k: "not", arg: this.neg() }; }
    return this.compare();
  }
  compare(): Node {
    let node = this.sum();
    const op = this.peekOp();
    if (op && COMPARE.has(op)) {
      this.take();
      node = { k: "bin", op, left: node, right: this.sum() };
      const again = this.peekOp();
      if (again && COMPARE.has(again)) {
        throw new ExprError("Vergleichsketten wie a < b < c gehen nicht -- " +
                            "bitte mit 'und' verbinden: a < b und b < c");
      }
    }
    return node;
  }
  sum(): Node {
    let node = this.term();
    for (let op = this.peekOp(); op === "+" || op === "-"; op = this.peekOp()) {
      this.take();
      node = { k: "bin", op, left: node, right: this.term() };
    }
    return node;
  }
  term(): Node {
    let node = this.unary();
    for (let op = this.peekOp(); op === "*" || op === "/" || op === "mod"; op = this.peekOp()) {
      this.take();
      node = { k: "bin", op, left: node, right: this.unary() };
    }
    return node;
  }
  unary(): Node {
    if (this.peekOp() === "-") { this.take(); return { k: "neg", arg: this.unary() }; }
    if (this.peekOp() === "+") { this.take(); return this.unary(); }
    const [kind, val] = this.take();
    if (kind === "num") return { k: "num", value: val as number };
    if (kind === "name") return { k: "var", name: val as string };
    if (kind === "op" && val === "(") {
      const node = this.expr();
      const close = this.atEnd() ? null : this.take();
      if (!close || close[0] !== "op" || close[1] !== ")") throw new ExprError("Fehlende schließende Klammer");
      return node;
    }
    if (kind === "text") throw new ExprError("Texte in Anführungszeichen sind nur in Ausgabe-Boxen erlaubt");
    throw new ExprError(`Unerwartetes ${show([kind, val])}`);
  }
}

type Kind = "zahl" | "bedingung";

export function kind(node: Node): Kind {
  switch (node.k) {
    case "num": case "var": return "zahl";
    case "text": throw new ExprError("Text ist hier nicht erlaubt");
    case "neg":
      need(node.arg, "zahl", "Vor einem Minus muss eine Zahl stehen");
      return "zahl";
    case "not":
      need(node.arg, "bedingung", "'nicht' braucht eine Bedingung, z. B. nicht (a > b)");
      return "bedingung";
    case "bin":
      if (ARITH.has(node.op) || COMPARE.has(node.op)) {
        need(node.left, "zahl", `Links von '${node.op}' muss eine Zahl stehen, keine Bedingung`);
        need(node.right, "zahl", `Rechts von '${node.op}' muss eine Zahl stehen, keine Bedingung`);
        return ARITH.has(node.op) ? "zahl" : "bedingung";
      } else {
        const word = node.op === "and" ? "und" : "oder";
        const msg = `'${word}' verbindet Bedingungen, z. B. a > 0 ${word} b > 0`;
        need(node.left, "bedingung", msg);
        need(node.right, "bedingung", msg);
        return "bedingung";
      }
  }
}

function need(node: Node, want: Kind, message: string): void {
  if (kind(node) !== want) throw new ExprError(message);
}

// ------------------------------------------------------------------------- Box-Inhalte

const ASSIGN_RE = new RegExp(`^\\s*(${NAME})\\s*(?::=|←|<-|=(?!=))([\\s\\S]*)$`);
const INPUT_PREFIX = /^\s*(?:eingabe|lies)\b\s*:?\s*(?=\S)/i;
const OUTPUT_PREFIX = /^\s*(?:ausgabe|ausgeben|gib\s+aus)\b\s*:?\s*(?=\S)/i;

function checkName(name: string): string {
  if (!NAME_RE.test(name)) throw new ExprError(`'${name}' ist kein gültiger Variablenname`);
  if (name.toLowerCase() in WORDS) throw new ExprError(`'${name}' ist ein reserviertes Wort und kein Variablenname`);
  return name;
}

function full(text: string, want: Kind): Node {
  const p = new Parser(text);
  if (p.atEnd()) throw new ExprError("Die Box ist leer");
  const node = p.expr();
  p.expectEnd();
  if (kind(node) !== want) {
    throw new ExprError(want === "bedingung" ? "Hier wird eine Bedingung erwartet, z. B. a > b"
                                             : "Hier wird eine Zahl erwartet, keine Bedingung");
  }
  return node;
}

export function parseAssignment(text: string): [string, Node] {
  const m = ASSIGN_RE.exec(text);
  if (!m) throw new ExprError("Verarbeitung braucht eine Zuweisung, z. B. x = x + 1");
  return [checkName(m[1]), full(m[2], "zahl")];
}

export function parseCondition(text: string): Node {
  const t = text.trimEnd();
  return full(t.endsWith("?") ? t.slice(0, -1) : t, "bedingung");
}

export function parseInput(text: string): string[] {
  const body = text.replace(INPUT_PREFIX, "").trim();
  if (!body) throw new ExprError("Eingabe braucht mindestens einen Variablennamen, z. B. a, b");
  const raw = body.split(",").map(n => n.trim());
  if (!raw.every(Boolean)) throw new ExprError("Zwischen den Kommas fehlt ein Variablenname");
  const names = raw.map(checkName);
  if (new Set(names).size !== names.length) throw new ExprError("Dieselbe Variable wird mehrfach eingelesen");
  return names;
}

export function parseOutput(text: string): Node[] {
  const p = new Parser(text.replace(OUTPUT_PREFIX, ""));
  if (p.atEnd()) throw new ExprError("Ausgabe braucht mindestens einen Wert, z. B. max");
  const items: Node[] = [];
  for (;;) {
    const tok = p.peek();
    const nxt = p.tokens[p.i + 1];
    if (tok && tok[0] === "text" && (!nxt || (nxt[0] === "op" && nxt[1] === ","))) {
      p.take();
      items.push({ k: "text", value: tok[1] as string });
    } else {
      const node = p.expr();
      if (kind(node) !== "zahl") throw new ExprError("Ausgegeben werden können Zahlen und Texte, keine Bedingungen");
      items.push(node);
    }
    if (p.atEnd()) return items;
    const sep = p.take();
    if (sep[0] !== "op" || sep[1] !== ",") {
      throw new ExprError(`Unerwartetes ${show(sep)} -- mehrere Werte mit Komma trennen`);
    }
  }
}

// ------------------------------------------------------------------------- Auswertung

function checked(v: number): number {
  if (Math.abs(v) > MAX_INT) throw new RunError("Zahl zu groß (mehr als 9 Billiarden)");
  return v === 0 ? 0 : v; // kein -0
}

export function intDiv(a: number, b: number): number {
  if (b === 0) throw new RunError("Division durch 0");
  return checked(Number(BigInt(a) / BigInt(b))); // BigInt schneidet zur 0 hin ab, exakt
}

export function evaluate(node: Node, env: Map<string, number>): number | boolean {
  switch (node.k) {
    case "num": return node.value;
    case "var": {
      const v = env.get(node.name);
      if (v === undefined) throw new RunError(`Variable ${node.name} hat noch keinen Wert`);
      return v;
    }
    case "text": throw new Error("Text kann nicht ausgewertet werden");
    case "neg": return checked(-(evaluate(node.arg, env) as number));
    case "not": return !evaluate(node.arg, env);
    case "bin": {
      if (node.op === "and") return (evaluate(node.left, env) as boolean) && (evaluate(node.right, env) as boolean);
      if (node.op === "or") return (evaluate(node.left, env) as boolean) || (evaluate(node.right, env) as boolean);
      const a = evaluate(node.left, env) as number;
      const b = evaluate(node.right, env) as number;
      switch (node.op) {
        case "+": return checked(a + b);
        case "-": return checked(a - b);
        case "*": return checked(a * b);
        case "/": return intDiv(a, b);
        case "mod": intDiv(a, b); return checked(a % b); // % ist für ganze Zahlen exakt, Vorzeichen wie a
        case "==": return a === b;
        case "!=": return a !== b;
        case "<": return a < b;
        case "<=": return a <= b;
        case ">": return a > b;
        case ">=": return a >= b;
      }
    }
  }
  throw new Error(`Unbekannter Ausdruck ${JSON.stringify(node)}`);
}

// ------------------------------------------------------------------------- Diagramm

type Code = [string, Node] | string[] | Node[] | Node;

export interface Program {
  problems: Problem[];
  types: Map<string, string>;
  code: Map<string, Code>;
  succ: Map<string, string>; // Ausgang -> Zielknoten
  start: string | null;
  reachable: string[];
  unreachable: string[];
  hasLoop: boolean;
  boxCount: number;
}

export function exits(type: string, id: string): string[] {
  if (type === "ende") return [];
  if (type === "entscheidung") return [`${id}.ja`, `${id}.nein`];
  return [id];
}

/** Übersetzt einen Box-Inhalt; wirft ExprError (für die Live-Prüfung beim Tippen). */
export function compileBox(type: string, text: string): Code | null {
  switch (type) {
    case "verarbeitung": return parseAssignment(text);
    case "eingabe": return parseInput(text);
    case "ausgabe": return parseOutput(text);
    case "entscheidung": return parseCondition(text);
  }
  return null;
}

const isBox = (t: string | undefined) => (BOX_TYPES as readonly string[]).includes(t ?? "");

export function compileFlow(flow: Flow): Program {
  const prog: Program = {
    problems: [], types: new Map(), code: new Map(), succ: new Map(), start: null,
    reachable: [], unreachable: [], hasLoop: false, boxCount: 0,
  };
  const problems = prog.problems;
  const P = (code: string, message: string, ref: string | null = null) => problems.push({ code, message, ref });
  const texts = new Map<string, string>();

  for (const n of flow.nodes ?? []) {
    const id = String(n.id ?? ""), type = String(n.type ?? "").toLowerCase();
    if (!id || id.includes(".")) P("bad_id", `Ungültige Knoten-ID '${id}'`, id);
    else if (prog.types.has(id)) P("duplicate_id", `Knoten-ID '${id}' doppelt`, id);
    else if (!(NODE_TYPES as readonly string[]).includes(type)) P("unknown_type", `Unbekannter Knotentyp '${type}'`, id);
    else { prog.types.set(id, type); texts.set(id, String(n.text || "")); }
  }

  const starts = [...prog.types].filter(([, t]) => t === "start").map(([id]) => id);
  if (!starts.length) P("no_start", "Das Diagramm braucht einen Start");
  else if (starts.length > 1) starts.slice(1).forEach(s => P("multiple_start", "Es darf nur einen Start geben", s));
  else prog.start = starts[0];

  const validExits = new Set([...prog.types].flatMap(([id, t]) => exits(t, id)));
  for (const e of flow.edges ?? []) {
    const src = String(e.from ?? ""), dst = String(e.to ?? "");
    if (!validExits.has(src)) {
      const gid = src.split(".")[0];
      if (prog.types.get(gid) === "ende") P("edge_from_end", "Nach dem Ende geht es nicht weiter", gid);
      else if (prog.types.has(gid)) P("bad_branch", `Ungültiger Ausgang '${src}'`, src);
      else P("unknown_source", `Unbekannter Pfeil-Anfang '${src}'`, src);
      continue;
    }
    if (!prog.types.has(dst)) { P("unknown_target", `Unbekanntes Pfeil-Ziel '${dst}'`, dst); continue; }
    if (prog.types.get(dst) === "start") { P("edge_into_start", "Kein Pfeil darf zum Start führen", dst); continue; }
    if (prog.succ.has(src) && prog.succ.get(src) !== dst) {
      P("multiple_edges", "Von hier gehen mehrere Pfeile ab -- nur einer erlaubt", src);
      continue;
    }
    prog.succ.set(src, dst);
  }

  if (prog.start === null) {
    prog.unreachable = [...prog.types.keys()];
    return prog;
  }

  const seen = new Set([prog.start]);
  const queue = [prog.start];
  for (let q = 0; q < queue.length; q++) {
    const id = queue[q];
    for (const out of exits(prog.types.get(id)!, id)) {
      const nxt = prog.succ.get(out);
      if (nxt !== undefined && !seen.has(nxt)) { seen.add(nxt); queue.push(nxt); }
    }
  }
  prog.reachable = queue;
  prog.unreachable = [...prog.types.keys()].filter(id => !seen.has(id));

  for (const id of prog.reachable) {
    const type = prog.types.get(id)!;
    for (const out of exits(type, id)) {
      if (!prog.succ.has(out)) {
        const branch = out.split(".")[1];
        P("open_exit", branch
          ? `Der ${branch[0].toUpperCase() + branch.slice(1)}-Ausgang der Entscheidung führt nirgendwohin`
          : "Von dieser Box führt kein Pfeil weiter", out);
      }
    }
    if (isBox(type)) {
      try {
        prog.code.set(id, compileBox(type, texts.get(id)!)!);
      } catch (err) {
        if (!(err instanceof ExprError)) throw err;
        P("syntax", err.message, id);
      }
    }
  }

  if (!prog.reachable.some(id => prog.types.get(id) === "ende")) {
    P("no_end", "Das Ende ist vom Start aus nicht erreichbar");
  }
  prog.hasLoop = hasCycle(prog);
  prog.boxCount = prog.reachable.filter(id => isBox(prog.types.get(id))).length;
  return prog;
}

function hasCycle(prog: Program): boolean {
  const state = new Map<string, 1 | 2>();
  for (const root of prog.reachable) {
    if (state.has(root)) continue;
    const stack: [string, string[], number][] = [[root, exits(prog.types.get(root)!, root), 0]];
    state.set(root, 1);
    while (stack.length) {
      const top = stack[stack.length - 1];
      if (top[2] >= top[1].length) { state.set(top[0], 2); stack.pop(); continue; }
      const nxt = prog.succ.get(top[1][top[2]++]);
      if (nxt === undefined) continue;
      if (state.get(nxt) === 1) return true;
      if (!state.has(nxt)) {
        state.set(nxt, 1);
        stack.push([nxt, exits(prog.types.get(nxt)!, nxt), 0]);
      }
    }
  }
  return false;
}

// ------------------------------------------------------------------------- Ausführung

export type RunStatus = "läuft" | "beendet" | "endlosschleife" | "laufzeitfehler";

/** Schrittweise Ausführung eines fehlerfreien Programms (prog.problems leer). */
export class Runner {
  cur: string;               // Knoten, der als Nächstes ausgeführt wird (am Ende: der Ende-Knoten)
  lastExit: string | null = null; // zuletzt benutzter Ausgang (für die Pfeil-Markierung)
  env = new Map<string, number>();
  ausgabe: number[] = [];
  texte: string[] = [];
  schritte = 0;
  status: RunStatus = "läuft";
  fehler: string | null = null;
  ref: string | null = null;
  private pos = 0;
  private readonly prog: Program;
  private readonly inputs: number[];
  private readonly maxSteps: number;

  constructor(prog: Program, inputs: number[], maxSteps: number) {
    this.prog = prog;
    this.inputs = inputs;
    this.maxSteps = maxSteps;
    this.cur = prog.start!;
  }

  private moveTo(exit: string): void {
    this.lastExit = exit;
    this.cur = this.prog.succ.get(exit)!;
    if (this.prog.types.get(this.cur) === "ende") this.status = "beendet";
  }

  step(): void {
    if (this.status !== "läuft") return;
    const type = this.prog.types.get(this.cur)!;
    if (type === "start") return this.moveTo(this.cur);
    if (this.schritte >= this.maxSteps) {
      this.status = "endlosschleife";
      this.ref = this.cur;
      this.fehler = `Nach ${this.maxSteps} Schritten noch nicht fertig -- Endlosschleife?`;
      return;
    }
    this.schritte++;
    const code = this.prog.code.get(this.cur)!;
    try {
      switch (type) {
        case "verarbeitung": {
          const [name, node] = code as [string, Node];
          this.env.set(name, evaluate(node, this.env) as number);
          break;
        }
        case "eingabe":
          for (const name of code as string[]) {
            if (this.pos >= this.inputs.length) {
              throw new RunError(`Keine Eingabe mehr für ${name} (es gibt nur ${this.inputs.length})`);
            }
            this.env.set(name, this.inputs[this.pos++]);
          }
          break;
        case "ausgabe":
          for (const item of code as Node[]) {
            if (item.k === "text") { this.texte.push(item.value); continue; }
            const v = evaluate(item, this.env) as number;
            this.ausgabe.push(v);
            this.texte.push(String(v));
          }
          break;
        case "entscheidung":
          return this.moveTo(`${this.cur}.${evaluate(code as Node, this.env) ? "ja" : "nein"}`);
      }
    } catch (err) {
      if (!(err instanceof RunError)) throw err;
      this.status = "laufzeitfehler";
      this.fehler = err.message;
      this.ref = this.cur;
      return;
    }
    this.moveTo(this.cur);
  }

  runToEnd(): this {
    while (this.status === "läuft") this.step();
    return this;
  }
}

// ------------------------------------------------------------------------- Prüfen

export type CaseStatus = "richtig" | "falsch" | "endlosschleife" | "laufzeitfehler";
export interface CaseResult {
  status: CaseStatus; ausgabe: number[]; texte: string[];
  fehler: string | null; ref: string | null; schritte: number;
}
export interface CheckResult {
  solved: boolean; problems: Problem[]; testfaelle: CaseResult[] | null;
  falsche_testfaelle: number[]; boxen: number; par_erreicht: boolean;
  schleife: boolean; unerreichbar: string[];
}

export function boxCounts(flow: Flow): Map<string, number> {
  const counts = new Map<string, number>();
  for (const n of flow.nodes ?? []) {
    const t = String(n.type ?? "").toLowerCase();
    if (isBox(t)) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return counts;
}

export const BOX_LABEL: Record<string, string> = {
  verarbeitung: "Verarbeitung", eingabe: "Eingabe", ausgabe: "Ausgabe", entscheidung: "Entscheidung",
  start: "Start", ende: "Ende",
};

export function check(task: Task, flow: Flow): CheckResult {
  const problems: Problem[] = [];
  for (const [type, n] of boxCounts(flow)) {
    const limit = task.boxen[type];
    if (limit !== undefined && n > limit) {
      problems.push({ code: "too_many_boxes", ref: type,
                      message: limit ? `Höchstens ${limit}× ${type} erlaubt`
                                     : `${BOX_LABEL[type]} ist in dieser Aufgabe nicht erlaubt` });
    }
  }
  const prog = compileFlow(flow);
  problems.push(...prog.problems);
  const result: CheckResult = {
    solved: false, problems, testfaelle: null, falsche_testfaelle: [], boxen: prog.boxCount,
    par_erreicht: false, schleife: prog.hasLoop, unerreichbar: prog.unreachable,
  };
  if (prog.problems.length) return result;

  result.testfaelle = task.testfaelle.map(tf => {
    const r = new Runner(prog, tf.eingabe, task.max_schritte).runToEnd();
    let status = r.status as CaseStatus;
    if (r.status === "beendet") {
      status = r.ausgabe.length === tf.ausgabe.length && r.ausgabe.every((v, i) => v === tf.ausgabe[i])
        ? "richtig" : "falsch";
    }
    return { status, ausgabe: r.ausgabe, texte: r.texte, fehler: r.fehler, ref: r.ref, schritte: r.schritte };
  });
  result.falsche_testfaelle = result.testfaelle.flatMap((r, i) => (r.status === "richtig" ? [] : [i]));
  result.solved = !problems.length && !result.falsche_testfaelle.length;
  result.par_erreicht = result.solved && (task.par === null || prog.boxCount <= task.par);
  return result;
}

/** Sterne wie in GateLab: ★ gelöst, ★★ mit ≤ par Boxen, ★★★ zusätzlich ohne Hinweis. */
export function starsFor(r: CheckResult, hintsUsed: number): number {
  if (!r.solved) return 0;
  return 1 + (r.par_erreicht ? 1 : 0) + (r.par_erreicht && hintsUsed === 0 ? 1 : 0);
}
